import { Component, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CircularGraphComponent } from '@shared/components/circular-graph/circular-graph.component';
import { ICircularGraphData } from '@shared/components/circular-graph/circular-graph.interface';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import { IDatatableConfiguration, TDatatableColumn } from '@shared/components/datatable/datatable.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { StackedBarsComponent } from '@shared/deliverables/stacked-bars/stacked-bars.component';
import { IStackedBarSegment } from '@shared/deliverables/stacked-bars/stacked-bars.interface';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  buildRankedCircularGraphData,
  buildRankedCircularGraphNameOverrides,
  EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES,
  EVENT_ANALYTICS_RANK_CIRCULAR_COLOR_MAP,
  rankTopEntities,
} from '../admin-event-analytics-ranking.util';
import {
  IApplicationPerformanceKpis,
  IApplicationPerformanceKpisPerUserGroup,
  IRankedEntity,
} from '../admin-event-analytics.interface';
import { AdminEventAnalyticsStore } from '../admin-event-analytics.store';
import { IGroupBreakdownRow } from './admin-event-analytics-groups.interface';

@Component({
  imports: [CircularGraphComponent, DatatableComponent, NgxSkeletonLoaderModule, StackedBarsComponent, TranslateModule],
  selector: 'app-admin-event-analytics-groups',
  styleUrl: './admin-event-analytics-groups.component.scss',
  templateUrl: './admin-event-analytics-groups.component.html',
})
export class AdminEventAnalyticsGroupsComponent {
  public readonly store = inject(AdminEventAnalyticsStore);

  public readonly rankColorMap = EVENT_ANALYTICS_RANK_CIRCULAR_COLOR_MAP;

  public readonly totalGroupsCount = computed(() => (this.store.kpisPerUserGroup$() ?? []).length);

  public readonly activeUsersSegments = computed<IStackedBarSegment[]>(() =>
    this.toSegments(this.rankByMetric((kpis) => kpis.activeUsers)),
  );

  public readonly campaignsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.campaignCreationsFinished),
  );
  public readonly reportsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.reportsCreated),
  );
  public readonly briefingsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.briefingsCreated),
  );

  public readonly campaignsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.campaignsCreatedRanking()),
  );
  public readonly reportsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.reportsCreatedRanking()),
  );
  public readonly briefingsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.briefingsCreatedRanking()),
  );

  public readonly campaignsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.campaignsCreatedRanking()),
  );
  public readonly reportsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.reportsCreatedRanking()),
  );
  public readonly briefingsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.briefingsCreatedRanking()),
  );

  // Sorted here (rather than relying solely on the datatable's own `defaultSort`) so the
  // underlying data source is always alphabetical ascending by name, regardless of the table's
  // interactive sort state (e.g. a persisted sort from a previous session).
  public readonly tableRows = computed<IGroupBreakdownRow[]>(() =>
    (this.store.kpisPerUserGroup$() ?? [])
      .map((entry) => this.buildRow(entry))
      .sort((a, b) => a.userGroupName.localeCompare(b.userGroupName)),
  );
  public readonly total = computed(() => this.tableRows().length);
  public readonly isTableDataLoaded = computed(() => this.store.kpisPerUserGroup$() !== null);

  public readonly tableColumns: TDatatableColumn<IGroupBreakdownRow, string>[] = [
    { id: 'userGroupName', isClientSideSearchable: true, isSortable: true, name: 'field.userGroup' },
    { id: 'activeUsers', isSortable: true },
    { id: 'campaignsStarted', isSortable: true },
    { id: 'campaignsFinished', isSortable: true },
    { dataViewStrategy: (row) => row.campaignCompletionSortValue, id: 'campaignCompletion', isSortable: true },
    { id: 'reportsCreated', isSortable: true },
    { dataViewStrategy: (row) => row.reportAdoptionSortValue, id: 'reportAdoption', isSortable: true },
    { id: 'briefingsCreated', isSortable: true },
    { dataViewStrategy: (row) => row.briefingAdoptionSortValue, id: 'briefingAdoption', isSortable: true },
    { dataViewStrategy: (row) => row.avgCompletionTimeSortValue, id: 'avgCompletionTime', isSortable: true },
  ];

  public readonly tableConfiguration: IDatatableConfiguration<IGroupBreakdownRow> = {
    defaultSort: { active: 'userGroupName', direction: 'asc' },
    isPaginationEnabled: true,
    isRowClickEnabled: false,
    isSearchEnabled: true,
  };

  // Column widths mirror `admin-event-analytics-groups.component.scss`'s `.mat-column-*` rules, so
  // the skeleton lines up exactly with the real table once it loads.
  public readonly tableLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        className: 'w-[19%]',
        columnPadding: '0 8px 0 16px',
        rowPadding: '18px 0',
        textWidth: '140px',
        type: 'text',
      },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '40px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[9%]', textWidth: '70px', type: 'text' },
    ],
    headerMargin: '4px 0 0 0',
    rows: 5,
    showHeaderControls: true,
    showPaginator: true,
    showSearchField: true,
  };

  private readonly durationUtilities = inject(DurationUtilitiesService);
  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);

  // Data is fetched by the parent `AdminEventAnalyticsComponent` (see its constructor) - owning
  // the fetch there instead of here means reopening this tab (which Angular Material's
  // `mat-tab-group` re-instantiates from scratch each time via `matTabContent`) never re-triggers
  // a redundant request or its loading flicker for data that's already loaded and hasn't gone stale.

  private rankByMetric(getValue: (kpis: IApplicationPerformanceKpis) => number): IRankedEntity[] {
    return rankTopEntities(
      (this.store.kpisPerUserGroup$() ?? []).map((entry) => ({
        id: entry.userGroupId,
        name: entry.userGroupName,
        value: getValue(entry.kpis),
      })),
    );
  }

  private toSegments(ranking: IRankedEntity[]): IStackedBarSegment[] {
    return ranking.map((entity, index) => ({
      colorClass: EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES[index],
      id: entity.id,
      label: entity.name,
      value: entity.value,
    }));
  }

  private buildRow(entry: IApplicationPerformanceKpisPerUserGroup): IGroupBreakdownRow {
    const { kpis } = entry;

    return {
      activeUsers: kpis.activeUsers,
      avgCompletionTime: this.durationUtilities.getDuration(kpis.avgTimeToCampaignCreationFinished, 'milliseconds'),
      avgCompletionTimeSortValue: kpis.avgTimeToCampaignCreationFinished,
      briefingAdoption: this.formatPercentage(kpis.briefingAdaptionConversion),
      briefingAdoptionSortValue: kpis.briefingAdaptionConversion,
      briefingsCreated: kpis.briefingsCreated,
      campaignCompletion: this.formatPercentage(kpis.campaignCompletionRate),
      campaignCompletionSortValue: kpis.campaignCompletionRate,
      campaignsFinished: kpis.campaignCreationsFinished,
      campaignsStarted: kpis.campaignCreationsStarted,
      id: entry.userGroupId,
      reportAdoption: this.formatPercentage(kpis.reportAdaptionConversion),
      reportAdoptionSortValue: kpis.reportAdaptionConversion,
      reportsCreated: kpis.reportsCreated,
      userGroupName: entry.userGroupName,
    };
  }

  private formatPercentage(value: number): string {
    // Guards against a non-finite backend value (e.g. NaN from a 0/0 division) rendering as "NaN%".
    const safeValue = Number.isFinite(value) ? value : 0;

    return `${this.numberSeparatorUtilities.formatNumberToLocaleString(safeValue * 100, null, 1, 1, true)}%`;
  }
}
